// TODO update this to import from commons/src/gql
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import React from 'react';
import I18n from 'i18n-js';
import { get } from 'lodash';
import classNames from 'classnames';

import ListingShippingDisplay, { ListingShippingDisplayFragment } from './listing_shipping_display';
import { PriceDisplay } from './PriceDisplay';
import RibbonView from './ribbon_view';
import { withUserContext, IUserContext } from './user_context_provider';
import {
  core_apimessages_CartItem as CartItem,
  core_apimessages_Listing_ListingType,
  core_apimessages_ListingPricing,
  Core_Listing_LayoutQuery,
} from '../gql/graphql';
import { RCIcon, RCTooltip } from '@reverbdotcom/cadence/components';
import { buildListingForBuyer, buildPrice } from '../listing_helpers';
import { inExperiment } from '../user_hooks';
import experiments from '../experiments';
import { InfoCircleIcon } from '@reverbdotcom/cadence/icons/react';
import { RCText, EyebrowTextProps } from '@reverbdotcom/cadence/components/RCText/RCText';

interface IExternalProps {
  listing: Core_Listing_LayoutQuery['listing'];
  inlineRibbon?: boolean;
  showCombinedRate?: boolean;
  useCombinedShippingStrikethrough?: boolean;
  cartItems?: CartItem[];
  hideOriginalPrice?: boolean;
  /** The small bit of UI under the thumbnail. Should showcase sale info, bump info, or things like that. */
  eyebrow?: {
    text: string;
    color?: EyebrowTextProps['color'];
  };
  redesign?: boolean;
}

type IProps = IExternalProps & IUserContext;

export const Item2PriceWithShippingCartItemFragment = gql`
  fragment Item2PriceWithShippingCartItemData on rql_Me {
    uuid
    cart {
      cartItems {
        _id
        shopId
        listingId
        subtotal {
          amountCents
          currency
        }
      }
    }
  }
`;

export const Item2PriceWithShippingFragment = gql`
  fragment Item2PriceWithShippingData on Listing {
    _id
    id
    state
    stateDescription
    taxIncluded
    taxIncludedHint
    listingType
    pricing {
      originalPrice {
        display
      }
      ribbon {
        display
        reason
      }
      buyerPrice {
        amountCents
        currency
        display
      }
      typicalNewPriceDisplay {
        amountDisplay
        descriptionDisplay
        savingsDisplay
      }
      originalPriceDescription
    }
    shop {
      _id
      id
    }
    ...ListingShippingDisplayData
  }
  ${ListingShippingDisplayFragment}
`;

export class PriceWithShipping extends React.Component<IProps, null> {
  get taxIncluded(): boolean {
    return get(this.props, 'listing.taxIncluded', false);
  }

  get taxIncludedHint(): string {
    return get(this.props, 'listing.taxIncludedHint', '');
  }

  get showIncludedTax(): boolean {
    return this.taxIncluded && this.taxIncludedHint !== '';
  }

  get isLive(): boolean {
    return get(this.props, 'listing.state', '') === 'live';
  }

  get pricing(): core_apimessages_ListingPricing {
    return get(this.props, 'listing.pricing', null);
  }

  isAuction(): boolean {
    return this.props.listing.listingType === core_apimessages_Listing_ListingType.AUCTION;
  }

  useTypicalNewPrice(): boolean {
    return inExperiment(this.props.user, experiments.WEB_TYPICAL_NEW_PRICE);
  }

  hasTypicalNewPrice(): boolean {
    return this.useTypicalNewPrice() && !!this.pricing.typicalNewPriceDisplay;
  }

  hasDiscount(): boolean {
    return !!this.pricing.originalPrice;
  }

  ribbonText(): string {
    if (this.hasTypicalNewPrice()) return this.pricing.typicalNewPriceDisplay.savingsDisplay;

    return this.pricing.ribbon ?
      this.pricing.ribbon.display :
      null;
  }

  priceDescription(): string {
    if (this.hasTypicalNewPrice()) return this.pricing.typicalNewPriceDisplay.descriptionDisplay;

    return this.pricing.originalPriceDescription;
  }

  renderOriginalPrice() {
    if ((this.hasDiscount()  || this.hasTypicalNewPrice()) && this.isLive && !this.props.hideOriginalPrice) {
      const decoratedListing = buildListingForBuyer(this.props.listing, this.props.user);
      const price = buildPrice(decoratedListing, false, false, this.props.user);
      const { discountPriceDisplay, originalPriceString } = price;
      const priceDescription = this.useTypicalNewPrice() ?
        this.priceDescription() :
        I18n.t('commons.priceWithShipping.originalPrice');

      const originalPriceClasses = classNames(
        { 'price-with-shipping__price__original': !this.props.redesign },
        { 'price-with-shipping__price__original--redesign': this.props.redesign },
      );

      return (
        <>
          {this.props.redesign && this.props.eyebrow &&
            <RCText.Eyebrow color={this.props.eyebrow.color}>{this.props.eyebrow.text}</RCText.Eyebrow>
          }
          <div className={originalPriceClasses}>
            <span className="visually-hidden">{priceDescription}</span>
            {discountPriceDisplay}
          </div>
          {this.props.redesign &&
            <>
              <div className="price-with-shipping__price__original-description">
                <span>{originalPriceString}</span>
              </div>
              {this.hasTypicalNewPrice() &&
                <RCTooltip
                  text={I18n.t('commons.priceWithShipping.typicalNewPriceTooltip')}
                >
                  <span className="color-secondary size-fixed-100 ml-1">
                    <RCIcon svgComponent={InfoCircleIcon} inline />
                  </span>
                </RCTooltip>
              }
            </>
          }
        </>
      );
    }

    return null;
  }

  renderPrice() {
    const hiddenText = () => {
      if (this.hasTypicalNewPrice()) return I18n.t('commons.priceWithShipping.currentPrice');

      if (this.hasDiscount()) return I18n.t('commons.priceWithShipping.newPrice');

      return I18n.t('commons.priceWithShipping.price');
    };

    return (
      <>
        <span className="visually-hidden">{hiddenText()}</span>
        <PriceDisplay
          amountCents={this.pricing.buyerPrice.amountCents}
          display={this.pricing.buyerPrice.display}
        />
      </>
    );
  }

  // Calculates what the buyer's running subtotal will
  // be with this listing's shop, if they were to add to cart.
  shopSubtotalForListingCents() {
    const { cartItems, listing } = this.props;
    const { shop } = listing;
    const buyerPriceCents = listing.pricing.buyerPrice.amountCents;
    if (!cartItems?.length) { return buyerPriceCents; }

    let shopSubtotalCents = 0;
    cartItems.forEach(item => {
      if (item.shopId === shop.id) {
        shopSubtotalCents += item.subtotal.amountCents;
      }
    });

    // NB: if the listing is already in the cart, clicking 'add to cart' does not
    // increase the quantity – therefore, only add the current listing's
    // amount to the subtotal if its not in the cart already
    if (!cartItems.some(item => item.listingId === listing.id)) {
      shopSubtotalCents += buyerPriceCents;
    }
    return shopSubtotalCents;
  }

  renderIncludedTax() {
    if (!this.showIncludedTax) {
      return null;
    }

    return (
      <div className="price-with-shipping__included-tax">
        {this.taxIncludedHint}
      </div>
    );
  }

  renderAcceptsOffers() {
    if (this.props.listing.listingType !== core_apimessages_Listing_ListingType.MAKE_AN_OFFER) {
      return null;
    }

    return (
      <div className="price-with-shipping__accepts-offers">
        {I18n.t('commons.acceptsOffers')}
      </div>
    );
  }

  renderRibbon() {
    if (!this.isLive) { return null; }

    const classes = classNames(
      { 'price-with-shipping__discount': !this.props.redesign },
      { 'mb-2': this.props.redesign },
    );

    return (
      <div className={classes}>
        <RibbonView
          auction={this.isAuction()}
          listingStateDescription={this.props.listing.stateDescription}
          listingStateSlug={this.props.listing.state}
          discountDisplay={this.ribbonText()}
          unstyled
          redesign={this.props.redesign}
        />
      </div>
    );
  }

  render() {
    return (
      <div className="price-with-shipping">
        <div className="price-with-shipping__price">
          {this.renderOriginalPrice()}
          {this.props.redesign && !this.props.inlineRibbon && this.renderRibbon()}
          <div className="price-with-shipping__price__amount">
            {this.renderPrice()}
            {this.props.inlineRibbon && this.renderRibbon()}
            {this.renderIncludedTax()}
          </div>
        </div>

        <ListingShippingDisplay
          listing={this.props.listing}
          shopSubtotalForListingCents={this.shopSubtotalForListingCents()}
          showCombinedRate={this.props.showCombinedRate}
          useCombinedShippingStrikethrough={this.props.useCombinedShippingStrikethrough}
        />

        {!this.props.redesign && !this.props.inlineRibbon && this.renderRibbon()}
      </div>
    );
  }
}

export default withUserContext(PriceWithShipping);
